import { memo, useCallback, useEffect, useState } from 'react';

import { useAtom, useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';

import { ActivityNavigation, ComparisonSwingModal, VisualizationsModal } from '@blocks';
import { useGestureOverride, useToggle } from '@hooks';
import { useTranslate } from '@i18n';
import { useUpdateUserSettings } from '@queries';
import {
    activeSwingIdAtom,
    activeUserIdAtom,
    activeUserSettingsAtom,
    activityIdAtom,
    playback3dCurrentViewPresetAtom,
    playback3dOverlayComparisonAtom,
    showVisualizationsAtom,
    useEnsureAtomValue,
} from '@store';
import {
    Above,
    BackArrowIcon,
    Heading2,
    IconButton,
    PropertyTrigger,
    Spinner,
    StatusBox,
    Text,
    ToggleButton,
    ToggleButtonV2,
} from '@ui';

import { ViewPresetsModal, VisualizerSection, getPresetLabel, type ViewPreset } from './components';
import { use3dDataStatus, useViewPresetCamera, useVisualizationParams } from './hooks';
import * as css from './Playback3D.css';

const MemoizedActivityNavigation = memo(ActivityNavigation);

type Playback3DProps = {
    getFullActivityLink: (activityId: number, swingId: string) => string;
};

export function Playback3D({ getFullActivityLink }: Playback3DProps) {
    const navigate = useNavigate();
    useGestureOverride();
    const [isVisualizationModalOpen, toggleVisualizationModal] = useToggle();
    const [comparison, toggleComparison] = useToggle();
    const [isViewPresetsModalOpen, setIsViewPresetsModalOpen] = useState(false);
    const [currentViewPreset, setCurrentViewPreset] = useAtom(playback3dCurrentViewPresetAtom);
    const [pendingPreset, setPendingPreset] = useState<ViewPreset | null>(null);
    const [overlayComparison, setOverlayComparison] = useAtom(playback3dOverlayComparisonAtom);
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    const activityId = useAtomValue(activityIdAtom);
    const activeSwingId = useEnsureAtomValue(activeSwingIdAtom);

    const { mutateAsync: upsertUserSettings } = useUpdateUserSettings(activeUserId);
    const handleToggleComparison = useCallback(
        (isActive: boolean) => {
            upsertUserSettings({ showComparison: isActive });
        },
        [upsertUserSettings],
    );

    const userSettings = useAtomValue(activeUserSettingsAtom);
    const { comparisonSwingId } = userSettings;
    const { noAnalysis, isLoadingAnalysis } = use3dDataStatus(activeSwingId);

    const { freezePosition, handleFreezePosition } = useVisualizationParams();

    useEffect(() => {
        if (comparisonSwingId) {
            handleFreezePosition(null);
        }
    }, [comparisonSwingId, handleFreezePosition]);

    const handleSelectActive = useCallback(
        (swingId: string) => {
            navigate(getFullActivityLink(activityId, swingId) + '/3d');
        },
        [navigate, activityId, getFullActivityLink],
    );

    const t = useTranslate();
    const [showVisualizations, setShowVisualizations] = useAtom(showVisualizationsAtom);

    const openViewPresetsModal = useCallback(() => setIsViewPresetsModalOpen(true), []);
    const closeViewPresetsModal = useCallback(() => setIsViewPresetsModalOpen(false), []);

    const handleApplyViewPreset = useCallback(
        (preset: ViewPreset) => {
            setCurrentViewPreset(preset);
            setPendingPreset(preset);
        },
        [setCurrentViewPreset],
    );

    const handlePresetApplied = useCallback(() => {
        setPendingPreset(null);
    }, []);

    const handleCameraInteraction = useCallback(() => {
        setCurrentViewPreset((current) => {
            if (current && !pendingPreset) {
                return null;
            }
            return current;
        });
    }, [pendingPreset, setCurrentViewPreset]);

    const handleClearViewPreset = useCallback(() => {
        setCurrentViewPreset(null);
    }, [setCurrentViewPreset]);

    const { effectiveCameraFocus, effectiveCameraFocusFrame, effectiveHorizontalOffset } =
        useViewPresetCamera(currentViewPreset);

    return (
        <div className={css.root}>
            <div className={css.mainContent}>
                <div className={css.sidebar}>
                    <div className={css.backNav}>
                        <IconButton onClick={() => navigate(getFullActivityLink(activityId, activeSwingId))}>
                            <BackArrowIcon />
                        </IconButton>
                        <Heading2>3D</Heading2>
                    </div>
                    <Above bp="md">
                        <div className={css.sidebarBody}>
                            <MemoizedActivityNavigation onSelectActive={handleSelectActive} />
                        </div>
                    </Above>
                </div>
                <div className={css.menuTriggers}>
                    <ToggleButtonV2
                        isActive={showVisualizations}
                        onChange={setShowVisualizations}
                        onClick={() => toggleVisualizationModal(true)}
                    >
                        <Text variant="h3">{t('common.visualizations')}</Text>
                    </ToggleButtonV2>
                    <ToggleButtonV2
                        isActive={userSettings.showComparison}
                        onChange={handleToggleComparison}
                        onClick={() => toggleComparison(true)}
                    >
                        <Text variant="h3">{t('common.comparison')}</Text>
                    </ToggleButtonV2>
                    <PropertyTrigger
                        label="Camera View"
                        values={[{ label: getPresetLabel(currentViewPreset) }]}
                        onClick={openViewPresetsModal}
                        onClear={currentViewPreset ? handleClearViewPreset : undefined}
                    />

                    {comparisonSwingId && (
                        <ToggleButton icon="comparison" isActive={overlayComparison} onChange={setOverlayComparison}>
                            Overlay Comparison
                        </ToggleButton>
                    )}
                </div>

                {isVisualizationModalOpen && <VisualizationsModal onClose={() => toggleVisualizationModal(false)} />}
                {isViewPresetsModalOpen && (
                    <ViewPresetsModal
                        open={isViewPresetsModalOpen}
                        onDismiss={closeViewPresetsModal}
                        onApply={handleApplyViewPreset}
                        currentPreset={currentViewPreset}
                    />
                )}
                {comparison && <ComparisonSwingModal open onDismiss={toggleComparison} />}
                {isLoadingAnalysis && !noAnalysis ? <Spinner size="l" isAbsolute /> : null}
                {!isLoadingAnalysis && noAnalysis ? (
                    <StatusBox
                        status="error"
                        position="absolute"
                        centerAbsolute
                        title="Missing Analysis"
                        description="Analysis could not be loaded for the active swing"
                    />
                ) : null}
                {!isLoadingAnalysis && !noAnalysis && (
                    <VisualizerSection
                        freezePosition={freezePosition}
                        effectiveCameraFocus={effectiveCameraFocus}
                        effectiveCameraFocusFrame={effectiveCameraFocusFrame}
                        effectiveHorizontalOffset={effectiveHorizontalOffset}
                        pendingPreset={pendingPreset}
                        onPresetApplied={handlePresetApplied}
                        onCameraInteraction={handleCameraInteraction}
                        onFreezePositionChange={handleFreezePosition}
                        overlayComparison={overlayComparison}
                    />
                )}
            </div>
        </div>
    );
}
